import { z } from 'zod';

import { positionSchema, sensorStatusSchema, sensorTypeSchema } from './sensor';
import { timeRangeSchema } from './sensorData';

const summarySchema = z.object({
  summary: z.string(),
  observations: z.string(),
  recommendations: z.string(),
});

export const reportPageOrientationSchema = z.enum(['portrait', 'landscape']);

export const dataReportChartTypeSchema = z.enum([
  'weather',
  'moisture',
  'woodMoisture',
  'bracketMoisture',
  'temperature',
  'relativeHumidity',
]);

export const dataReportVisibilitySchema = z.object({
  showScatter: z.boolean().optional(),
  expandedStatusGroupId: z.enum(['ok', 'warning', 'error']).nullable().optional(),
  visibleNormalSensorLocalIds: z.array(z.number()).nullish().default(null),
  visibleStatusGroupIds: z
    .array(z.enum(['ok', 'warning', 'error']))
    .nullish()
    .default(null),
  visibleStatusSensorLocalIdsByGroup: z.partialRecord(
    z.enum(['ok', 'warning', 'error']),
    z.array(z.number())
  ),
});

export const dataReportMapViewSchema = z.object({
  mode: z.enum(['periodSummary', 'now', 'reading']),
  timestamp: z.number().nullable().optional(),
});

export const dataReportMapMetricSchema = z.enum([
  'downtime',
  'moisture',
  'possibleCondensation',
  'relativeHumidity',
  'temperature',
  'woodMoisture',
]);

export const dataReportChartSchema = z.object({
  type: dataReportChartTypeSchema,
  include: z.boolean(),
  observations: z.string(),
  visibility: dataReportVisibilitySchema.optional(),
});

export const dataReportInstallationSchema = z.object({
  mapId: z.string().optional(),
  installationId: z.string(),
  observations: z.string(),
  rotateMap: z.boolean().optional().default(false),
  metric: dataReportMapMetricSchema.optional(),
  view: dataReportMapViewSchema.optional(),
});

export const dataReportConfigSchema = z.object({
  // Version 2 adds report sections while keeping saved v1 reports renderable.
  layoutVersion: z.literal(2).optional(),
  sectionOrder: z.array(z.string()).optional(),
  title: z.string().optional(),
  installationIds: z.array(z.string()).optional(),
  metrics: z.array(dataReportMapMetricSchema.exclude(['downtime'])).optional(),
  includeSensorCharts: z.boolean().optional(),
  sectionChartVisibility: z.record(z.string(), dataReportVisibilitySchema).optional(),
  scopeNeedsReview: z.boolean().optional(),
  sectionNotes: z
    .record(
      z.string(),
      z.object({
        observations: z.string(),
        recommendations: z.string(),
      })
    )
    .optional(),
  conclusion: z.string().optional(),
  analysisMode: z.enum(['moisture', 'relativeHumidity', 'temperature']),
  chartMode: z.enum(['normal', 'status']),
  charts: z.array(dataReportChartSchema),
  connectGaps: z.boolean(),
  dataMapping: z.object({
    include: z.boolean(),
    installationId: z.string(),
    metric: dataReportMapMetricSchema,
    view: dataReportMapViewSchema.optional(),
    observations: z.string(),
    rotateMap: z.boolean().optional().default(false),
  }),
  weather: z
    .object({
      include: z.boolean(),
      observations: z.string().optional(),
    })
    .optional(),
  periodSummary: z
    .object({
      include: z.boolean(),
      installations: z.array(dataReportInstallationSchema),
    })
    .optional(),
  sensorData: z
    .object({
      observations: z.string(),
    })
    .optional(),
  downtime: z.object({
    include: z.boolean(),
    observations: z.string(),
    includeChart: z.boolean().optional(),
    installations: z.array(dataReportInstallationSchema).optional(),
  }),
  sensorGroupKey: z.string(),
  sensorIds: z.array(z.number()),
  sensorLocalIds: z.array(z.number()),
  visibility: dataReportVisibilitySchema,
});

export const reportPartPlotSchema = z.object({
  include: z.boolean(),
  includeLegend: z.boolean().optional(),
  highlightedDataKeyLabel: z.string().optional(),
  observations: z.string(),
});

// some configs must be optional because the are not present in already generated reports
export const partSchema = z.object({
  partId: z.number(),
  partName: z.string().optional(),
  installationIds: z.array(z.string()),
  sensorStates: z.array(sensorStatusSchema),
  sensorTypes: z.array(sensorTypeSchema),
  sensorPositions: z.array(positionSchema),
  sensorIds: z.array(z.string()).optional(),
  plots: z.object({
    scatterMoisture: reportPartPlotSchema,
    lineMoisture: reportPartPlotSchema,
    areaPrecipitation: reportPartPlotSchema,
    lineRelativeHumidity: reportPartPlotSchema,
    lineSensorTemperature: reportPartPlotSchema,
    barWeatherTemperature: reportPartPlotSchema,
    barRelativeHumidityMoisture: reportPartPlotSchema,
  }),
  summary: summarySchema,
});

// TODO: specify the schema arrays
// some configs must be optional because the are not present in already generated reports
export const configsSchema = z.object({
  // Persisted report format identifiers are shared with the API and saved documents.
  reportType: z.enum(['legacy', 'analyticsV2']).optional(),
  snapshotId: z.string().uuid().optional(),
  pageOrientation: reportPageOrientationSchema.optional(),
  analyticsV2: dataReportConfigSchema.optional(),
  maps: z.array(z.any()).optional(),
  manuals: z.array(z.any()).optional(),
  parts: z.array(partSchema).optional(),
  reports: z.array(z.any()).optional(),
  summary: summarySchema,
  timeRange: timeRangeSchema,
});

export const documentationSchema = z.object({
  documentationId: z.number(),
  projectId: z.number(),
  filename: z.string(),
  configs: configsSchema,
  status: z.enum(['draft', 'generating', 'generated', 'failed']).optional(),
  createdAt: z.string(),
  generatedAt: z.string().nullable(),
  generationStartedAt: z.string().nullable().optional(),
  fileSize: z.number().nullable(),
});

export const oldReportSchema = z.object({
  filepath: z.string(),
  filename: z.string(),
  size: z.number().optional(),
  lastModified: z.string().optional(),
});

export type GenerateInstallationPlanBody = z.infer<typeof generateInstallationPlanBodySchema>;
export const generateInstallationPlanBodySchema = z.object({
  language: z.string().optional(),
  filename: z.string(),
  includeInstallationInstructions: z.boolean().optional(),
  plans: z.array(
    z.object({
      installationId: z.string(),
      comment: z.string().optional(),
    })
  ),
});

export type InstallationPlanConfigs = z.infer<typeof generateInstallationPlanBodySchema>;

export type GenerateAsBuiltBody = z.infer<typeof generateAsBuiltBodySchema>;
export const generateAsBuiltBodySchema = z.object({
  language: z.string().optional(),
  filename: z.string(),
  moveToOperative: z.boolean().optional(),
  plans: z.array(
    z.object({
      installationId: z.string(),
      comment: z.string().optional(),
    })
  ),
});

export type S3File = z.infer<typeof s3FileSchema>;
export const s3FileSchema = z.object({
  filepath: z.string(),
  filename: z.string(),
  size: z.number().optional(),
  lastModified: z.string().optional(),
});

export type InstallationPlan = z.infer<typeof installationPlanSchema>;
export const installationPlanSchema = z.object({
  filepath: z.string(),
  filename: z.string(),
  size: z.number().optional(),
  lastModified: z.string().optional(),
  configs: generateInstallationPlanBodySchema.nullable().optional(),
});

export type Documentation = z.infer<typeof documentationSchema>;
export type DocumentationConfigs = z.infer<typeof configsSchema>;
export type DataReportInstallation = z.infer<typeof dataReportInstallationSchema>;
export type DataReportChart = z.infer<typeof dataReportChartSchema>;
export type DataReportChartType = z.infer<typeof dataReportChartTypeSchema>;
export type DataReportConfig = z.infer<typeof dataReportConfigSchema>;
export type ReportPageOrientation = z.infer<typeof reportPageOrientationSchema>;
export type Summary = z.infer<typeof summarySchema>;
export type OldReport = z.infer<typeof oldReportSchema>;
export type ReportPart = z.infer<typeof partSchema>;
export type ReportPartPlot = z.infer<typeof reportPartPlotSchema>;

export type DocumentationFileDeviceType = z.infer<typeof documentationFileDeviceTypeSchema>;
export const documentationFileDeviceTypeSchema = z.enum(['sensor', 'gateway', 'ventCap', 'other']);

export type UploadDocumentationBody = z.infer<typeof uploadDocumentationSchema>;
export const uploadDocumentationSchema = z.object({
  imageFile: z.instanceof(File).optional(),
  deviceType: documentationFileDeviceTypeSchema,
  localId: z.number().min(1).optional(),
  comment: z.string().max(1000),
});

export type DocumentationFile = z.infer<typeof documentationFileSchema>;
export const documentationFileSchema = z.object({
  documentationFileId: z.number(),
  projectId: z.number(),
  filesize: z.number().nullable(),
  createdAt: z.string(),
  deviceType: documentationFileDeviceTypeSchema,
  localId: z.number().nullable(),
  comment: z.string(),
  userName: z.string().nullable(),
});

export type DataReportMapView = z.infer<typeof dataReportMapViewSchema>;
export type DataReportVisibility = z.infer<typeof dataReportVisibilitySchema>;
